import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { setRoutingState } from '@adornis/router/client/routing-state-signal.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html } from 'lit';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { recommendedJobPositions } from '../../../api/matching-score.js';
import { JobPosition } from '../../../db/job-position.js';
import { CtaPillButton } from '../../components/cta-pill-button.js';
import { JobCard } from './job-card.js';
import { RegisterOnboardingFinish } from './register-onboarding-finish.js';

export const RegisterOnboardingSelectOtherJobs = stateful(
  ({ onFinished, onBack }: { onFinished: () => void; onBack: () => void }) => {
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const user = useObservable(() => currentUser(), []);

    // Get recommended jobs based on user's matching score
    const recommendedJobsQuery = useQuery(() => {
      if (!user?._id) return of([]);
      return recommendedJobPositions(user._id)(selectionSet(() => JobPosition, 2));
    }, [user?._id]);

    const toggle = (id: string, checked: boolean) => {
      const next = new Set(selected);
      checked ? next.add(id) : next.delete(id);
      setSelected(next);
    };

    return html`
      <div class="space-y-4">
        ${RegisterOnboardingFinish()}

        <div class="card bg-base-100">
          <div class="card-body">
            <div class="text-center text-sm md:text-base">
              ${m.register_onboarding_select_other_jobs_intro_title()}
              <br />
              ${m.register_onboarding_select_other_jobs_intro_desc()}
            </div>
          </div>
        </div>

        ${
          recommendedJobsQuery.loading
            ? html`
                <div class="flex justify-center py-8">
                  <span class="loading loading-spinner loading-lg"></span>
                </div>
              `
            : html`
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                  ${
                    recommendedJobsQuery.value?.length
                      ? recommendedJobsQuery.value.map(job =>
                          JobCard({
                            job,
                            isSelected: selected.has(job._id),
                            onToggle: toggle,
                            hideSelector: false,
                          }),
                        )
                      : html`<div class="col-span-3 text-center py-8">
                          ${m.register_onboarding_select_other_jobs_empty()}
                        </div>`
                  }
                </div>
              `
        }

        <div class="mt-2 flex justify-end">
          ${CtaPillButton({
            label: m.next(),
            onClick: async () => {
              await setRoutingState({
                path: NO_CHANGE,
                searchParams: new URLSearchParams({ jobs: Array.from(selected).join(',') }),
              });
              onFinished();
            },
          })}
        </div>
      </div>
    `;
  },
);
